/**
 * Evaluate a doc math expression: + - * / ( ) and {{variable}} refs.
 * Decimal-scaled arithmetic so 1+1+1+1 is 4 and 0.1+0.2 is 0.3 (no IEEE tail).
 */

import { isArrayAllIndex, parseVariableRef } from './variableScope';

const BRACE_VAR_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** After {{vars}} are substituted, only arithmetic / numbers remain. */
const SAFE_EXPR_RE = /^[\d.\s+\-*/()eE]+$/;

const normalizeOps = (raw) =>
  String(raw ?? '')
    .replace(/×/g, '*')
    .replace(/[÷]/g, '/')
    .replace(/[−–]/g, '-');

const pow10 = (n) => {
  let x = 1;
  for (let i = 0; i < n; i += 1) x *= 10;
  return x;
};

const placesOf = (raw) => {
  const s = String(raw).trim().toLowerCase().replace(/^-/, '');
  const [mant, expRaw] = s.split('e');
  const exp = expRaw != null && expRaw !== '' ? Number(expRaw) : 0;
  if (!Number.isFinite(exp)) return 0;
  const dot = mant.indexOf('.');
  const frac = dot === -1 ? 0 : mant.length - dot - 1;
  return Math.max(0, frac - exp);
};

const scaled = (value, places) => Math.round(value * pow10(places));

const addSub = (a, b, sign) => {
  const places = Math.max(a.places, b.places);
  const value =
    (scaled(a.value, places) + sign * scaled(b.value, places)) / pow10(places);
  return { value, places };
};

const mul = (a, b) => {
  const places = a.places + b.places;
  const value = (scaled(a.value, a.places) * scaled(b.value, b.places)) / pow10(places);
  return { value, places };
};

const div = (a, b) => {
  if (b.value === 0) return null;
  // Keep a little extra scale so 1/8 stays 0.125, then display trims.
  const places = Math.min(10, Math.max(a.places, b.places) + 6);
  const value = a.value / b.value;
  if (!Number.isFinite(value)) return null;
  return { value: scaled(value, places) / pow10(places), places };
};

/** Stable display — integers stay integers; leftover zeros get stripped. */
export const formatMathResult = (n) => {
  if (!Number.isFinite(n)) return '';
  const x = Number(n);
  if (Math.abs(x - Math.round(x)) < 1e-9) return String(Math.round(x));
  return String(Number(x.toFixed(10)));
};

/**
 * + − × ÷ ( ) over decimal-scaled numbers. Returns NaN-ish null on bad input.
 */
export const evaluateDecimalExpr = (raw) => {
  const source = String(raw ?? '').replace(/\s+/g, '');
  if (!source) return null;
  let i = 0;

  const peek = () => source[i] || '';
  const eat = (ch) => {
    if (peek() !== ch) return false;
    i += 1;
    return true;
  };

  const readNumber = () => {
    const start = i;
    if (source[i] === '+' || source[i] === '-') i += 1;
    let sawDigit = false;
    let sawDot = false;
    while (i < source.length) {
      const ch = source[i];
      if (ch >= '0' && ch <= '9') {
        sawDigit = true;
        i += 1;
        continue;
      }
      if (ch === '.' && !sawDot) {
        sawDot = true;
        i += 1;
        continue;
      }
      break;
    }
    if ((source[i] === 'e' || source[i] === 'E') && sawDigit) {
      i += 1;
      if (source[i] === '+' || source[i] === '-') i += 1;
      const expStart = i;
      while (i < source.length && source[i] >= '0' && source[i] <= '9') i += 1;
      if (i === expStart) return null;
    }
    const token = source.slice(start, i);
    if (!sawDigit) return null;
    const value = Number(token);
    if (!Number.isFinite(value)) return null;
    return { value, places: placesOf(token) };
  };

  const parseFactor = () => {
    if (eat('(')) {
      const inner = parseExpr();
      if (!inner || !eat(')')) return null;
      return inner;
    }
    const unaryMinus = eat('-');
    if (!unaryMinus) eat('+');
    if (peek() === '(') {
      const inner = parseFactor();
      if (!inner) return null;
      return unaryMinus ? { value: -inner.value, places: inner.places } : inner;
    }
    const num = readNumber();
    if (!num) return null;
    return unaryMinus ? { value: -num.value, places: num.places } : num;
  };

  const parseTerm = () => {
    let left = parseFactor();
    if (!left) return null;
    while (peek() === '*' || peek() === '/') {
      const op = peek();
      i += 1;
      const right = parseFactor();
      if (!right) return null;
      left = op === '*' ? mul(left, right) : div(left, right);
      if (!left) return null;
    }
    return left;
  };

  const parseExpr = () => {
    let left = parseTerm();
    if (!left) return null;
    while (peek() === '+' || peek() === '-') {
      const sign = eat('+') ? 1 : (eat('-') ? -1 : 0);
      const right = parseTerm();
      if (!right) return null;
      left = addSub(left, right, sign);
    }
    return left;
  };

  const result = parseExpr();
  if (!result || i !== source.length) return null;
  return result.value;
};

const asFiniteNumber = (raw) => {
  const value = String(raw ?? '').trim();
  if (!value) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * @returns {{
 *   ok: boolean,
 *   state: 'resolved'|'empty'|'unresolved',
 *   text: string,
 *   value?: number,
 *   color: string,
 *   tip: string,
 * }}
 */
export const evaluateDocMath = (raw, resolveVariable) => {
  const source = normalizeOps(raw).trim();
  if (!source) {
    return {
      ok: false,
      state: 'empty',
      text: 'math',
      color: '',
      tip: 'Type a formula — + − × ÷ and {{variables}}.',
    };
  }

  const refs = [];
  BRACE_VAR_RE.lastIndex = 0;
  const expr = source.replace(BRACE_VAR_RE, (match, rawName) => {
    const { name, ref, index } = parseVariableRef(rawName);
    if (!name) {
      refs.push({ ref: String(rawName || '').trim(), ok: false });
      return 'NaN';
    }
    if (isArrayAllIndex(index)) {
      refs.push({ ref, ok: false });
      return 'NaN';
    }
    const hit = resolveVariable?.(ref) || null;
    const rawValue = String(hit?.value ?? '').trim();
    const num = hit ? asFiniteNumber(rawValue) : null;
    if (num == null) {
      refs.push({ ref, hit, ok: false });
      return 'NaN';
    }
    refs.push({ ref, hit, ok: true, num });
    // Keep the author's digits so 0.1 stays 0.1 (not an IEEE 0.10000000000000001).
    return `(${rawValue})`;
  });

  const failed = refs.filter((r) => !r.ok);
  if (failed.length) {
    const first = failed[0].ref || 'variable';
    const seen = new Set();
    const names = [];
    for (const item of failed) {
      const ref = String(item.ref || '').trim();
      if (!ref || seen.has(ref)) continue;
      seen.add(ref);
      names.push(`{{${ref}}}`);
    }
    return {
      ok: false,
      state: 'unresolved',
      text: names.length ? names.join(' ') : source,
      color: '',
      tip: `{{${first}}} is not a number in this context.`,
    };
  }

  if (!SAFE_EXPR_RE.test(expr)) {
    return {
      ok: false,
      state: 'unresolved',
      text: source,
      color: '',
      tip: `${source} — use + − × ÷ and brackets only.`,
    };
  }

  const value = evaluateDecimalExpr(expr);
  if (!Number.isFinite(value)) {
    return {
      ok: false,
      state: 'unresolved',
      text: source,
      color: '',
      tip: `${source} — cannot calculate.`,
    };
  }
  const text = formatMathResult(value);
  const color = refs.find((r) => r.hit?.color)?.hit.color || '';
  return {
    ok: true,
    state: 'resolved',
    text,
    value,
    color,
    tip: `${source} = ${text}`,
  };
};
